import type {
  FaireAcademyChapter,
  FaireAcademyGuide,
} from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { convertTitleToHash } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/utils";
import { Button } from "@faire/web--source/slate/Button";
import { IFaireAcademyChapter } from "@faire/web-api--source/indigofair/data/IFaireAcademyChapter";
import { IFaireAcademyGuide } from "@faire/web-api--source/indigofair/data/IFaireAcademyGuide";
import openWithFaire from "@faire/web-api--source/routes/www/open-with-faire";
import buyRetailInventory from "@faire/web-api--source/routes/www/open-with-faire/academy/buy-retail-inventory";
import { lazy } from "react";
import * as React from "react";

const LazyPlanning = lazy(() =>
  import(
    /* webpackChunkName: "plan-and-buy-inventory-planning" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/PlanAndBuyInventory/__internal__/Planning"
  ).then((m) => ({ default: m.Planning }))
);
const LazyFinancing = lazy(() =>
  import(
    /* webpackChunkName: "plan-and-buy-inventory-financing" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/PlanAndBuyInventory/__internal__/Financing"
  ).then((m) => ({ default: m.Financing }))
);
const LazySourcingAndBuying = lazy(() =>
  import(
    /* webpackChunkName: "plan-and-buy-inventory-sourcing-and-buying" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/PlanAndBuyInventory/__internal__/SourcingAndBuying"
  ).then((m) => ({ default: m.SourcingAndBuying }))
);
const LazyMerchandising = lazy(() =>
  import(
    /* webpackChunkName: "plan-and-buy-inventory-merchandising" */
    "@faire/retailer-visitor-shared/views/open-with-faire/academy/guideData/PlanAndBuyInventory/__internal__/Merchandising"
  ).then((m) => ({ default: m.Merchandising }))
);

const C1 = "Planning";
const PLANNING: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.PLANNING,
  title: C1,
  readTimeMinutes: 5,
  titleHash: convertTitleToHash(C1),
  ChapterComponent: LazyPlanning,
};

const C2 = "Financing";
const FINANCING: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.FINANCING,
  title: C2,
  readTimeMinutes: 3,
  titleHash: convertTitleToHash(C2),
  ChapterComponent: LazyFinancing,
};

const C3 = "Sourcing and buying";
const SOURCING_AND_BUYING: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.SOURCING_AND_BUYING,
  title: C3,
  readTimeMinutes: 6,
  titleHash: convertTitleToHash(C3),
  ChapterComponent: LazySourcingAndBuying,
};

const C4 = "Merchandising";
const MERCHANDISING: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.MERCHANDISING,
  title: C4,
  readTimeMinutes: 5,
  titleHash: convertTitleToHash(C4),
  ChapterComponent: LazyMerchandising,
};

const GUIDE_CHAPTERS: Array<FaireAcademyChapter> = [
  PLANNING,
  FINANCING,
  SOURCING_AND_BUYING,
  MERCHANDISING,
];

export const PLAN_AND_BUY_INVENTORY: FaireAcademyGuide = {
  id: IFaireAcademyGuide.GuideIdentifier.PLAN_AND_BUY_INVENTORY,
  title: "Plan & buy inventory",
  description:
    "Nail retail inventory planning, then go shopping (the fun part!)",
  cardSummary: "Nail inventory planning, and then go shopping (the fun part!)",
  guideSummary: (
    <>
      Effective inventory management is crucial for retail success. You need to
      know what you can afford to buy before knowing what you can sell, so start
      with a detailed budget. You should also consider inventory financing
      options, such as the 60-day payment terms that eligible retailers can
      access through{" "}
      <Button variant="tertiary" linkTo={openWithFaire()}>
        Open with Faire
      </Button>
      . Nail inventory planning, and you can move on to the fun part—shopping.
      The brands you decide to work with and the items you choose to sell will
      be the foundation of what makes your store unique, and there are a myriad
      of methods for discovering them.
    </>
  ),
  featuredImage:
    "https://cdn.faire.com/fastly/414810ecfbff11a159f842bca05e57189797b8ae2f05abb683de2fec6a5c4366.png",
  featuredImageAltText:
    "New inventory on the table of an independent grocery store",
  backgroundImage:
    "https://cdn.faire.com/fastly/b9ad6b7b396c185670cf528e8aa0660ab64a67e11a4ad5986db17ec288b47080.jpeg",
  readTimeMinutes: GUIDE_CHAPTERS.reduce(
    (partialReadTime, chapter) => partialReadTime + chapter.readTimeMinutes,
    0
  ),
  chapters: GUIDE_CHAPTERS,
  linkToPage: buyRetailInventory(),
  titleTag: "Planning, buying & merchandising: What retailers need to know",
};
